/**
 * PDF 답안지 생성 컴포넌트
 *
 * 기능:
 * - 페이지 선택 (다중 선택 가능)
 * - 옵션 체크박스 (이름란, 날짜란 포함 여부)
 * - 미리보기 기능
 * - 다운로드 기능
 * - 바로 프린트 기능
 */

import React, { useState, useMemo } from 'react';
import {
  Modal,
  Button,
  Select,
  Checkbox,
  Space,
  message,
  Spin,
  Divider,
  Typography,
  Alert,
  Input,
} from 'antd';
import {
  FilePdfOutlined,
  PrinterOutlined,
  DownloadOutlined,
  EyeOutlined,
} from '@ant-design/icons';
import type { Problem } from '../../types/workbook';
import {
  generateAnswerSheetPdf,
  generateMultiPagePdf,
  downloadPdf,
  printPdf,
  openPdfInNewTab,
} from '../../utils/pdfGenerator';

const { Title, Text } = Typography;

/**
 * AnswerSheetGenerator Props
 */
interface AnswerSheetGeneratorProps {
  /** 모달 열림 여부 */
  open: boolean;
  /** 문제집 이름 */
  workbookName: string;
  /** 모든 문제 목록 */
  problems: Problem[];
  /** 모달 닫기 콜백 */
  onClose: () => void;
}

/**
 * PDF 답안지 생성 컴포넌트
 */
export const AnswerSheetGenerator: React.FC<AnswerSheetGeneratorProps> = ({
  open,
  workbookName,
  problems,
  onClose,
}) => {
  // 선택된 페이지 목록
  const [selectedPages, setSelectedPages] = useState<number[]>([]);
  // 페이지 범위 입력
  const [rangeInput, setRangeInput] = useState('');
  // 옵션 상태
  const [includeStudentName, setIncludeStudentName] = useState(true);
  const [includeDate, setIncludeDate] = useState(true);
  // 로딩 상태
  const [isGenerating, setIsGenerating] = useState(false);

  /**
   * 사용 가능한 페이지 목록 계산
   */
  const availablePages = useMemo(() => {
    const pageSet = new Set(problems.map(p => p.page));
    return Array.from(pageSet).sort((a, b) => a - b);
  }, [problems]);

  /**
   * 선택된 페이지들의 문제 필터링
   */
  const getProblemsForPages = (pages: number[]): Map<number, Problem[]> => {
    const problemsByPage = new Map<number, Problem[]>();

    pages.forEach(page => {
      const pageProblems = problems.filter(p => p.page === page);
      if (pageProblems.length > 0) {
        problemsByPage.set(page, pageProblems);
      }
    });

    return problemsByPage;
  };

  /**
   * 전체 선택/해제
   */
  const handleSelectAll = () => {
    if (selectedPages.length === availablePages.length) {
      setSelectedPages([]);
    } else {
      setSelectedPages([...availablePages]);
    }
  };

  /**
   * 페이지 범위 문자열 파싱 및 적용
   * 지원 형식: "10-20", "10-20, 25-30", "5, 10-15, 20"
   */
  const handleApplyRange = () => {
    if (!rangeInput.trim()) {
      message.warning('페이지 범위를 입력해주세요.');
      return;
    }

    const newPages: Set<number> = new Set(selectedPages);
    const parts = rangeInput.split(',').map(p => p.trim());

    for (const part of parts) {
      if (part.includes('-')) {
        // 범위 형식: "10-20"
        const [startStr, endStr] = part.split('-').map(s => s.trim());
        const start = parseInt(startStr, 10);
        const end = parseInt(endStr, 10);

        if (!isNaN(start) && !isNaN(end) && start <= end) {
          for (let page = start; page <= end; page++) {
            if (availablePages.includes(page)) {
              newPages.add(page);
            }
          }
        }
      } else {
        // 단일 페이지: "5"
        const page = parseInt(part, 10);
        if (!isNaN(page) && availablePages.includes(page)) {
          newPages.add(page);
        }
      }
    }

    const sortedPages = Array.from(newPages).sort((a, b) => a - b);
    setSelectedPages(sortedPages);
    setRangeInput('');

    const addedCount = sortedPages.length - selectedPages.length;
    if (addedCount > 0) {
      message.success(`${addedCount}개 페이지가 추가되었습니다.`);
    } else {
      message.info('추가된 페이지가 없습니다. (이미 선택되었거나 범위 밖)');
    }
  };

  /**
   * PDF 미리보기
   */
  const handlePreview = async () => {
    if (selectedPages.length === 0) {
      message.warning('페이지를 선택해주세요.');
      return;
    }

    setIsGenerating(true);
    try {
      const problemsByPage = getProblemsForPages(selectedPages);

      if (selectedPages.length === 1) {
        // 단일 페이지
        const page = selectedPages[0];
        const pageProblems = problemsByPage.get(page) || [];
        const doc = await generateAnswerSheetPdf({
          workbookName,
          page,
          problems: pageProblems,
          includeStudentName,
          includeDate,
        });
        openPdfInNewTab(doc);
      } else {
        // 여러 페이지
        const doc = await generateMultiPagePdf(workbookName, problemsByPage, {
          includeStudentName,
          includeDate,
        });
        openPdfInNewTab(doc);
      }

      message.success('미리보기가 새 탭에서 열렸습니다.');
    } catch (error) {
      console.error('PDF 미리보기 오류:', error);
      message.error('PDF 생성 중 오류가 발생했습니다.');
    } finally {
      setIsGenerating(false);
    }
  };

  /**
   * PDF 다운로드
   */
  const handleDownload = async () => {
    if (selectedPages.length === 0) {
      message.warning('페이지를 선택해주세요.');
      return;
    }

    setIsGenerating(true);
    try {
      const problemsByPage = getProblemsForPages(selectedPages);

      // 파일명 생성
      const pageRange = selectedPages.length === 1
        ? `${selectedPages[0]}p`
        : `${Math.min(...selectedPages)}-${Math.max(...selectedPages)}p`;
      const filename = `${workbookName.replace(/\s+/g, '_')}_${pageRange}_답안지.pdf`;

      if (selectedPages.length === 1) {
        // 단일 페이지
        const page = selectedPages[0];
        const pageProblems = problemsByPage.get(page) || [];
        const doc = await generateAnswerSheetPdf({
          workbookName,
          page,
          problems: pageProblems,
          includeStudentName,
          includeDate,
        });
        downloadPdf(doc, filename);
      } else {
        // 여러 페이지
        const doc = await generateMultiPagePdf(workbookName, problemsByPage, {
          includeStudentName,
          includeDate,
        });
        downloadPdf(doc, filename);
      }

      message.success('PDF 다운로드가 시작되었습니다.');
    } catch (error) {
      console.error('PDF 다운로드 오류:', error);
      message.error('PDF 생성 중 오류가 발생했습니다.');
    } finally {
      setIsGenerating(false);
    }
  };

  /**
   * PDF 바로 프린트
   */
  const handlePrint = async () => {
    if (selectedPages.length === 0) {
      message.warning('페이지를 선택해주세요.');
      return;
    }

    setIsGenerating(true);
    try {
      const problemsByPage = getProblemsForPages(selectedPages);

      if (selectedPages.length === 1) {
        // 단일 페이지
        const page = selectedPages[0];
        const pageProblems = problemsByPage.get(page) || [];
        const doc = await generateAnswerSheetPdf({
          workbookName,
          page,
          problems: pageProblems,
          includeStudentName,
          includeDate,
        });
        printPdf(doc);
      } else {
        // 여러 페이지
        const doc = await generateMultiPagePdf(workbookName, problemsByPage, {
          includeStudentName,
          includeDate,
        });
        printPdf(doc);
      }

      message.success('프린트 창이 열렸습니다.');
    } catch (error) {
      console.error('PDF 프린트 오류:', error);
      message.error('PDF 생성 중 오류가 발생했습니다.');
    } finally {
      setIsGenerating(false);
    }
  };

  /**
   * 모달 닫기 시 상태 초기화
   */
  const handleClose = () => {
    setSelectedPages([]);
    setRangeInput('');
    setIncludeStudentName(true);
    setIncludeDate(true);
    onClose();
  };

  /**
   * 선택된 페이지의 총 문제 수 계산
   */
  const totalSelectedProblems = useMemo(() => {
    return selectedPages.reduce((sum, page) => {
      const count = problems.filter(p => p.page === page).length;
      return sum + count;
    }, 0);
  }, [selectedPages, problems]);

  return (
    <Modal
      title={
        <Space>
          <FilePdfOutlined />
          <span>PDF 답안지 생성</span>
        </Space>
      }
      open={open}
      onCancel={handleClose}
      width={600}
      footer={null}
    >
      <Spin spinning={isGenerating} tip="PDF 생성 중...">
        <div style={{ padding: '16px 0' }}>
          {/* 안내 메시지 */}
          <Alert
            message="PDF 답안지 안내"
            description={
              <ul style={{ margin: 0, paddingLeft: 20 }}>
                <li>문제집 1페이지 = PDF 1페이지로 생성됩니다.</li>
                <li>학생이 손글씨로 답안을 작성한 후 촬영하면 자동 채점됩니다.</li>
              </ul>
            }
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
          />

          {/* 문제집 이름 */}
          <Title level={5} style={{ marginBottom: 16 }}>
            {workbookName}
          </Title>

          {/* 페이지 선택 */}
          <div style={{ marginBottom: 16 }}>
            <Text strong>페이지 선택</Text>
            <div style={{ marginTop: 8 }}>
              <Space direction="vertical" style={{ width: '100%' }}>
                {/* 페이지 범위 입력 */}
                <Space.Compact style={{ width: '100%' }}>
                  <Input
                    placeholder="예: 10-20 또는 10-20, 25-30"
                    value={rangeInput}
                    onChange={(e) => setRangeInput(e.target.value)}
                    onPressEnter={handleApplyRange}
                    style={{ flex: 1 }}
                  />
                  <Button onClick={handleApplyRange}>범위 적용</Button>
                </Space.Compact>
                <Button
                  size="small"
                  onClick={handleSelectAll}
                >
                  {selectedPages.length === availablePages.length ? '전체 해제' : '전체 선택'}
                </Button>
                <Select
                  mode="multiple"
                  style={{ width: '100%' }}
                  placeholder="페이지를 선택하세요"
                  value={selectedPages}
                  onChange={setSelectedPages}
                  options={availablePages.map(page => {
                    const count = problems.filter(p => p.page === page).length;
                    return {
                      value: page,
                      label: `${page}페이지 (${count}문제)`,
                    };
                  })}
                  maxTagCount={5}
                  maxTagPlaceholder={(omittedValues) =>
                    `+${omittedValues.length}개 더...`
                  }
                />
              </Space>
            </div>
          </div>

          {/* 선택 정보 */}
          {selectedPages.length > 0 && (
            <div style={{ marginBottom: 16, color: '#1890ff' }}>
              <Text type="secondary">
                선택: {selectedPages.length}페이지, 총 {totalSelectedProblems}문제
              </Text>
            </div>
          )}

          <Divider />

          {/* 옵션 */}
          <div style={{ marginBottom: 24 }}>
            <Text strong>옵션</Text>
            <div style={{ marginTop: 8 }}>
              <Space direction="vertical">
                <Checkbox
                  checked={includeStudentName}
                  onChange={(e) => setIncludeStudentName(e.target.checked)}
                >
                  학생 이름 입력란 포함
                </Checkbox>
                <Checkbox
                  checked={includeDate}
                  onChange={(e) => setIncludeDate(e.target.checked)}
                >
                  날짜 입력란 포함
                </Checkbox>
              </Space>
            </div>
          </div>

          <Divider />

          {/* 액션 버튼들 */}
          <Space wrap style={{ width: '100%', justifyContent: 'center' }}>
            <Button
              icon={<EyeOutlined />}
              onClick={handlePreview}
              disabled={selectedPages.length === 0}
            >
              미리보기
            </Button>
            <Button
              icon={<DownloadOutlined />}
              onClick={handleDownload}
              disabled={selectedPages.length === 0}
            >
              다운로드
            </Button>
            <Button
              type="primary"
              icon={<PrinterOutlined />}
              onClick={handlePrint}
              disabled={selectedPages.length === 0}
            >
              바로 프린트
            </Button>
          </Space>
        </div>
      </Spin>
    </Modal>
  );
};
